import type {ReferenceCreationContext} from '../ReferenceCreationContext';
import type {PlaneReferenceRole, ReferenceEntitySemanticType,} from '../ReferenceFeatureTypes';
import type {PlaneFeatureDefinition} from './ReferencePlaneSolver';


/**
 * 基准面计算来源协议版本。
 */
export const REFERENCE_PLANE_CALCULATION_SOURCE_VERSION = 1;

/**
 * 基准面引用绑定来源类型。
 */
export enum ReferencePlaneCalculationReferenceBindingKind {
  /** 运行时场景对象引用。 */
  RuntimeObject = 'runtime-object',
  /** 装配体导入拓扑稳定引用。 */
  AssemblyTopology = 'assembly-topology',
  /** 基准几何引用。 */
  DatumReference = 'datum-reference',
}

/**
 * 基准面计算引用快照。
 */
export interface ReferencePlaneCalculationReference {
  /** 引用角色。 */
  role: PlaneReferenceRole;
  /** 运行时实体 ID。 */
  entityId: string;
  /** 实体类型。 */
  entityType: ReferenceEntitySemanticType;
  /** 显示标签。 */
  label: string;
  /** 绑定来源类型。 */
  bindingKind: ReferencePlaneCalculationReferenceBindingKind;
  /** 原始场景对象 UUID，仅用于排查，不作为稳定真相。 */
  sourceObjectUuid?: string;
  /** 原始场景对象名称，仅用于排查，不作为稳定真相。 */
  sourceObjectName?: string;
  /** 选择层解析出的拓扑命中快照。 */
  topologyPick?: ResolvedTopologyPick | null;
  /** 装配体导入拓扑稳定引用。 */
  assemblyTopologyRef?: AssemblyTopologySelectionRef | null;
}

/**
 * 基准面计算来源快照。
 *
 * 设计说明：
 * 1. DatumPlane 必须知道自己由哪些引用和约束计算出来，才能在装配体组件移动后重算；
 * 2. 运行时 `uuid/name` 只能用于排查，稳定真相优先使用 `AssemblyTopologySelectionRef`；
 * 3. 该结构只保存可序列化数据，不持有任何 `THREE.Object3D` 引用。
 */
export interface ReferencePlaneCalculationSource {
  /** 来源协议版本。 */
  schemaVersion: typeof REFERENCE_PLANE_CALCULATION_SOURCE_VERSION;
  /** 基准面特征 ID。 */
  featureId: string;
  /** 创建上下文。 */
  creationContext: ReferenceCreationContext;
  /** 创建该平面的完整求解定义。 */
  definition: PlaneFeatureDefinition;
  /** 参与计算的引用快照。 */
  references: ReferencePlaneCalculationReference[];
  /** 创建时激活的解索引。 */
  selectedSolutionIndex: number;
  /** 多基准面输出时当前平面的输出索引。 */
  generatedPlaneIndex: number;
  /** 多基准面输出时本次生成的平面总数。 */
  generatedPlaneCount: number;
}

/**
 * 克隆数组。
 *
 * @param value 原始数组。
 * @returns 克隆数组；不存在时返回 `undefined`。
 */
function cloneOptionalArray<T>(value: T[] | undefined): T[] | undefined {
  return value ? [...value] : undefined;
}

/**
 * 克隆运行时拓扑命中快照。
 *
 * @param pick 原始拓扑命中快照。
 * @returns 克隆后的拓扑命中快照。
 */
function cloneResolvedTopologyPick(pick: ResolvedTopologyPick | null | undefined): ResolvedTopologyPick | null {
  if (!pick) {
    return null;
  }
  return {
    ...pick,
    adjacentFaceRefs: cloneOptionalArray(pick.adjacentFaceRefs),
    runtimeTopologyHint: {
      ...pick.runtimeTopologyHint,
      adjacentFaceRefs: cloneOptionalArray(pick.runtimeTopologyHint.adjacentFaceRefs),
      adjacentFaceHints: pick.runtimeTopologyHint.adjacentFaceHints
        ? pick.runtimeTopologyHint.adjacentFaceHints.map((hint) => ({ ...hint }))
        : undefined,
    },
  };
}

/**
 * 克隆装配体拓扑稳定引用。
 *
 * @param ref 原始稳定引用。
 * @returns 克隆后的稳定引用。
 */
function cloneAssemblyTopologyRef(
  ref: AssemblyTopologySelectionRef | null | undefined,
): AssemblyTopologySelectionRef | null {
  if (!ref) {
    return null;
  }
  return {
    ...ref,
    adjacentFaceRefs: cloneOptionalArray(ref.adjacentFaceRefs),
  };
}

/**
 * 克隆基准面计算来源。
 *
 * @param source 原始计算来源。
 * @returns 克隆后的计算来源。
 */
export function cloneReferencePlaneCalculationSource(
  source: ReferencePlaneCalculationSource | null | undefined,
): ReferencePlaneCalculationSource | null {
  if (!source) {
    return null;
  }
  return {
    ...source,
    definition: {
      ...source.definition,
      refs: source.definition.refs.map((ref) => ({
        ...ref,
        entityRef: {
          ...ref.entityRef,
          topologyPick: cloneResolvedTopologyPick(ref.entityRef.topologyPick),
          assemblyTopologyRef: cloneAssemblyTopologyRef(ref.entityRef.assemblyTopologyRef),
        },
        flags: { ...ref.flags },
      })),
    },
    references: source.references.map((reference) => ({
      ...reference,
      topologyPick: cloneResolvedTopologyPick(reference.topologyPick),
      assemblyTopologyRef: cloneAssemblyTopologyRef(reference.assemblyTopologyRef),
    })),
  };
}

